'use client';

import { useState } from 'react';
import {
  useFloating,
  useHover,
  useInteractions,
  offset,
  shift,
  autoUpdate,
  FloatingPortal,
} from '@floating-ui/react';

type Props = {
  currentAmount: number;
  targetAmount: number;
  isProfile?: boolean;
};

export const ProgressBar = ({
  currentAmount,
  targetAmount,
  isProfile,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);

  const percentage = Math.min((currentAmount / targetAmount) * 100, 100);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    placement: 'top',
  });

  const hover = useHover(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  return (
    <div className="w-full bg-gray-200 rounded-full h-2 relative">
      <div
        className="bg-blue-primary h-1.5 rounded-full transition-all duration-300"
        style={{ width: `${percentage}%` }}
      />
      <div
        ref={refs.setReference}
        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-4 h-4 bg-blue-primary border-2 border-white rounded-full shadow-sm transition-all duration-300"
        style={{ left: `${percentage}%` }}
        {...(isProfile ? getReferenceProps() : {})}
      >
        {isProfile && (
          <FloatingPortal>
            {isOpen && (
              <div
                ref={refs.setFloating}
                style={floatingStyles}
                {...getFloatingProps()}
                className="bg-white text-black rounded-md custom-shadow z-50 h-7 p-0.5 pr-1"
              >
                <span className="text-xs font-normal h-6 w-8 bg-blue-primary p-1 rounded-sm mr-2">
                  {Math.round(percentage)}%
                </span>
                <span className="text-xs font-light text-black/40 py-1.5 pr-2">
                  ${currentAmount} / ${targetAmount}
                </span>
              </div>
            )}
          </FloatingPortal>
        )}
      </div>
    </div>
  );
};
